import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Button } from "components/ui/button";

import {
  CAMPAIGN_FOCUS_RING_CLASS,
  CAMPAIGN_MUTED_TEXT_CLASS,
  CAMPAIGN_SECONDARY_ACTION_CLASS,
  CAMPAIGN_SECTION_NOTE_CLASS,
} from "../constants/campaignTheme";
import {
  CHARITY_CENTRED_STATE_CLASS,
  CHARITY_COLUMN_CLASS,
  CHARITY_PAGE_CLASS,
  CHARITY_PAGE_FOOTER_CLASS,
  CHARITY_PAGE_LEAD_CLASS,
  CHARITY_PAGE_TITLE_CLASS,
  CHARITY_PANEL_CLASS,
  CHARITY_TABLE_CELL_CLASS,
  CHARITY_TABLE_FRAME_CLASS,
  CHARITY_TABLE_HEAD_CELL_CLASS,
} from "../constants/charityPageTheme";
import { LEADERBOARD_BOARD_TABS, leaderboardBoardHref, parseLeaderboardBoard } from "../constants/leaderboardBoards";
import { CharityApiService } from "../services/CharityApiService";
import {
  CharityRoute,
  LeaderboardAdult,
  LeaderboardBoard,
  LeaderboardData,
  LeaderboardHousehold,
  LeaderboardMinor,
} from "../types";

const BOARD_ROW_CLASS = "border-b border-[#2C2C30] last:border-b-0 hover:bg-[#1C1C1F]";

const BOARD_RANK_CELL_CLASS = `${CHARITY_TABLE_CELL_CLASS} font-extrabold tabular-nums`;

const BOARD_FIGURE_CELL_CLASS = `${CHARITY_TABLE_CELL_CLASS} text-right font-semibold tabular-nums`;

const BOARD_EMPTY_CELL_CLASS = `${CHARITY_TABLE_CELL_CLASS} py-10 text-center ${CAMPAIGN_MUTED_TEXT_CLASS}`;

const BOARDS_UNAVAILABLE_MESSAGE = "The boards could not be read just now.";

// Switching board is not an action on the campaign, so it takes none of the
// action roles — but it wears the same focus ring as everything else.
const tabClassName = (isActive: boolean): string =>
  `-mb-px border-b-2 px-1 pb-3 text-[12px] font-semibold uppercase tracking-[0.09em] transition motion-reduce:transition-none ${CAMPAIGN_FOCUS_RING_CLASS} ${
    isActive ? "border-[#E66E1A] text-[#E66E1A]" : "border-transparent text-[#A2A09C] hover:text-[#F6F4F0]"
  }`;

/**
 * Leaderboards page — the campaign home for a signed-in participant.
 *
 * Three boards: households (ranked on adult mileage, with minor ride
 * count shown beside it), adults (miles), and minors (ride count, never
 * distance).
 *
 * The page carries no navigation of its own. It used to offer a row of buttons
 * to reporting, the ride list and the household, each of which the header
 * already offers a few pixels above — the same destinations twice, and after
 * the household moved under the ride list, two of the three were stale.
 *
 * Which board is open is held in the URL hash rather than in component state,
 * so a board can be linked to and survives a refresh. Holding it in state made
 * every board share one address: a participant sent "the adults board" opened
 * the households one, and a refresh mid-read dropped them back to it.
 */
export const LeaderboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const activeBoard = parseLeaderboardBoard(hash);
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadLeaderboard = async () => {
      try {
        const boardData = await CharityApiService.getLeaderboardAsync();

        // Sign-in lands here, so a payload missing any one board would crash
        // every participant on arrival rather than one page for one person.
        if (
          !Array.isArray(boardData?.households) ||
          !Array.isArray(boardData?.adults) ||
          !Array.isArray(boardData?.minors)
        ) {
          throw new Error(BOARDS_UNAVAILABLE_MESSAGE);
        }

        setLeaderboardData(boardData);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load leaderboard");
      } finally {
        setIsLoading(false);
      }
    };

    loadLeaderboard();

    const interval = setInterval(loadLeaderboard, 60000);
    return () => clearInterval(interval);
  }, []);

  if (isLoading) {
    return (
      <div className={CHARITY_PAGE_CLASS}>
        <div className={CHARITY_CENTRED_STATE_CLASS}>
          <p className={CAMPAIGN_MUTED_TEXT_CLASS}>Loading leaderboard…</p>
        </div>
      </div>
    );
  }

  // Keep the page when the boards cannot be loaded, rather than replacing it
  // with the exception. Before the campaign is configured this is the ordinary
  // state, not a fault, and a visitor should never be shown the transport-level
  // wording of a failed request.
  if (error) {
    return (
      <div className={CHARITY_PAGE_CLASS}>
        <div className={CHARITY_COLUMN_CLASS}>
          <div className="mb-10">
            <h1 className={CHARITY_PAGE_TITLE_CLASS}>Leaderboards</h1>
            <p className={CHARITY_PAGE_LEAD_CLASS}>Updated every minute</p>
          </div>
          <div className={`${CHARITY_PANEL_CLASS} py-12 text-center`}>
            <p className={`mb-1 text-[15.5px] ${CAMPAIGN_MUTED_TEXT_CLASS}`}>Updating…</p>
            <p className={CAMPAIGN_SECTION_NOTE_CLASS}>The boards appear here once the campaign is under way.</p>
            <Button
              variant="outline"
              onClick={() => window.location.reload()}
              className={`${CAMPAIGN_SECONDARY_ACTION_CLASS} mt-6`}
            >
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={CHARITY_PAGE_CLASS}>
      <div className={CHARITY_COLUMN_CLASS}>
        <div className="mb-8">
          <h1 className={CHARITY_PAGE_TITLE_CLASS}>Leaderboards</h1>
          <p className={CHARITY_PAGE_LEAD_CLASS}>Updated every minute</p>
        </div>

        {/* Links rather than buttons: each board has an address now, so a
            participant can copy one out of the browser and send it. */}
        <nav aria-label="Leaderboards" className="mb-6 flex gap-4 border-b border-[#2C2C30] sm:gap-6">
          {LEADERBOARD_BOARD_TABS.map((tab) => (
            <Link
              key={tab.board}
              to={leaderboardBoardHref(tab.board)}
              aria-current={activeBoard === tab.board ? "page" : undefined}
              className={tabClassName(activeBoard === tab.board)}
            >
              {tab.label}
            </Link>
          ))}
        </nav>

        {activeBoard === LeaderboardBoard.Households && leaderboardData && (
          <div className={CHARITY_TABLE_FRAME_CLASS}>
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#2C2C30] bg-[#1C1C1F]">
                  <th className={`${CHARITY_TABLE_HEAD_CELL_CLASS} text-left`}>Rank</th>
                  <th className={`${CHARITY_TABLE_HEAD_CELL_CLASS} text-left`}>Household</th>
                  <th className={`${CHARITY_TABLE_HEAD_CELL_CLASS} text-right`}>Adult Miles</th>
                  <th className={`${CHARITY_TABLE_HEAD_CELL_CLASS} text-right`}>Minor Rides</th>
                </tr>
              </thead>
              <tbody>
                {leaderboardData.households.length === 0 ? (
                  <tr>
                    <td colSpan={4} className={BOARD_EMPTY_CELL_CLASS}>
                      No households riding yet
                    </td>
                  </tr>
                ) : (
                  leaderboardData.households.map((household: LeaderboardHousehold) => (
                    <tr key={household.joinNumber} className={BOARD_ROW_CLASS}>
                      <td className={BOARD_RANK_CELL_CLASS}>#{household.rank}</td>
                      <td className={CHARITY_TABLE_CELL_CLASS}>{household.displayName}</td>
                      <td className={BOARD_FIGURE_CELL_CLASS}>{household.miles.toFixed(1)} mi</td>
                      <td className={`${BOARD_FIGURE_CELL_CLASS} font-normal ${CAMPAIGN_MUTED_TEXT_CLASS}`}>
                        {household.minorRideCount} {household.minorRideCount === 1 ? "ride" : "rides"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeBoard === LeaderboardBoard.Adults && leaderboardData && (
          <div className={CHARITY_TABLE_FRAME_CLASS}>
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#2C2C30] bg-[#1C1C1F]">
                  <th className={`${CHARITY_TABLE_HEAD_CELL_CLASS} text-left`}>Rank</th>
                  <th className={`${CHARITY_TABLE_HEAD_CELL_CLASS} text-left`}>Rider</th>
                  <th className={`${CHARITY_TABLE_HEAD_CELL_CLASS} text-right`}>Miles</th>
                </tr>
              </thead>
              <tbody>
                {leaderboardData.adults.length === 0 ? (
                  <tr>
                    <td colSpan={3} className={BOARD_EMPTY_CELL_CLASS}>
                      No adults riding yet
                    </td>
                  </tr>
                ) : (
                  leaderboardData.adults.map((adult: LeaderboardAdult) => (
                    <tr key={adult.rank} className={BOARD_ROW_CLASS}>
                      <td className={BOARD_RANK_CELL_CLASS}>#{adult.rank}</td>
                      <td className={CHARITY_TABLE_CELL_CLASS}>{adult.displayName}</td>
                      <td className={BOARD_FIGURE_CELL_CLASS}>{adult.miles.toFixed(1)} mi</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeBoard === LeaderboardBoard.Minors && leaderboardData && (
          <div className={CHARITY_TABLE_FRAME_CLASS}>
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#2C2C30] bg-[#1C1C1F]">
                  <th className={`${CHARITY_TABLE_HEAD_CELL_CLASS} text-left`}>Rank</th>
                  <th className={`${CHARITY_TABLE_HEAD_CELL_CLASS} text-left`}>Rider</th>
                  <th className={`${CHARITY_TABLE_HEAD_CELL_CLASS} text-right`}>Rides</th>
                </tr>
              </thead>
              <tbody>
                {leaderboardData.minors.length === 0 ? (
                  <tr>
                    <td colSpan={3} className={BOARD_EMPTY_CELL_CLASS}>
                      No minors riding yet
                    </td>
                  </tr>
                ) : (
                  leaderboardData.minors.map((minor: LeaderboardMinor) => (
                    <tr key={minor.rank} className={BOARD_ROW_CLASS}>
                      <td className={BOARD_RANK_CELL_CLASS}>#{minor.rank}</td>
                      <td className={CHARITY_TABLE_CELL_CLASS}>{minor.displayName}</td>
                      <td className={BOARD_FIGURE_CELL_CLASS}>
                        {minor.rideCount} {minor.rideCount === 1 ? "ride" : "rides"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <p className={`mt-4 ${CAMPAIGN_SECTION_NOTE_CLASS}`}>
          Every rider taking part appears here. Nobody outside the campaign can be inferred from the rankings.
        </p>

        <div className={CHARITY_PAGE_FOOTER_CLASS}>
          <Button
            onClick={() => navigate(CharityRoute.Campaign)}
            variant="outline"
            className={`${CAMPAIGN_SECONDARY_ACTION_CLASS} w-full sm:w-auto`}
          >
            Back to Campaign
          </Button>
        </div>
      </div>
    </div>
  );
};
